// Admin tab: Metro Tariff Import.
// Upload a Custom Logistic metro trucking rate sheet (per-suburb port<->door rates:
// "Monada <Port> <MODE> Sheet") and turn it into Monada rates bound to a
// postcode/named-location door. Parse in-browser -> convertMetroTariff -> preview
// -> /api/rates/bulk into the chosen workspace.
//
// Sheet columns: POSTCODE/ZIPCODE | NAMED LOCATION | PORT | COUNTRY | MODE |
//   LCL: MINIMUM FEE | PER M3 | PER TONNE | CURRENCY
//   FCL: 20' | 40' | CURRENCY
//   AIR: MINIMUM FEE | PER KG | CURRENCY
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';

import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';

import {
    Box, Typography, Button, CircularProgress, Alert, Card, CardContent,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, TextField,
    Table, TableBody, TableRow, TableCell, TableHead, TableContainer, Paper,
    FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, FormHelperText,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp } from '@fortawesome/pro-light-svg-icons';

import { convertMetroTariff } from '../dashboard/rates/metroTariff.js';
import { readMetroTariffSheet } from '../dashboard/rates/metroTariffSheet.js';
import { PORT_CODE_MAP, AIRPORT_CODE_MAP, AIRPORT_COUNTRY, isAirSheet } from '../dashboard/rates/metroPorts.js';
import { getRatesFromServer } from '../dashboard/utils/rates.js';

const PREVIEW_LIMIT = 200;
const CHUNK = 25;

function readArrayBuffer(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(new Error('Failed to read ' + file.name));
        r.readAsArrayBuffer(file);
    });
}

// Extract the display string for a charge field (a formula or "value|CUR").
function fieldDisplay(f) {
    if (!f) return '';
    const cell = f.values?.[Object.keys(f.values || {})[0]];
    if (!cell) return '';
    return cell.formula ? cell.formula : (cell.value != null ? `${cell.value}${cell.currency ? '|' + cell.currency : ''}` : '');
}

function findField(rate, title) {
    for (const s of (rate.offer?.sections || [])) {
        for (const f of (s.offers?.[0]?.fields || [])) if (f.title === title) return f;
    }
    return null;
}

function MetroTariff() {
    const { accesstoken } = useOutletContext();
    const [excelFile, setExcelFile] = useState(null);
    const [rows, setRows] = useState(null);
    const [sheetInfo, setSheetInfo] = useState(null); // { ports:[], modes:[], rowCount }
    const [processing, setProcessing] = useState(null);
    const [error, setError] = useState(null);

    // Config
    const [portName, setPortName] = useState('');
    const [portCode, setPortCode] = useState('');
    const [fuelPct, setFuelPct] = useState(33);
    const [fclBookingFee, setFclBookingFee] = useState(690);
    const [bookingCurrency, setBookingCurrency] = useState('AUD');
    const [validFrom, setValidFrom] = useState('');
    const [validUntil, setValidUntil] = useState('');
    // Every priced row makes a rate each way — port/airport to door and back. Both is the normal
    // import and stays the default; either one alone is for topping up a tariff that only moved in
    // one direction, without duplicating the leg that is already loaded.
    const [directions, setDirections] = useState(['import', 'export']);
    // The carriers' own header block, read off the sheet: supplier, code, validity, service.
    const [sheetMeta, setSheetMeta] = useState(null);
    const [supplierName, setSupplierName] = useState('');

    const [result, setResult] = useState(null); // { rates, counts, errors }
    const [rates, setRates] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);

    const [orgs, setOrgs] = useState(null);
    const [orgsError, setOrgsError] = useState(null);
    const [orgId, setOrgId] = useState('');
    const [adding, setAdding] = useState(null);
    const [addResult, setAddResult] = useState(null);
    const [overrideExisting, setOverrideExisting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios(`/api/admin/organizations?accesstoken=${accesstoken}`);
                setOrgs(data);
                setOrgsError(null);
            } catch (e) {
                // Without this list the workspace picker is disabled and the rates have
                // nowhere to go, which read as the page being broken for no stated reason.
                setOrgsError(e?.response?.data?.detail || e.message || 'Could not load the workspace list.');
            }
        })();
    }, [accesstoken]);

    const onDrop = useCallback(async (acceptedFiles) => {
        setError(null); setResult(null); setAddResult(null);
        const file = acceptedFiles.find(f => /\.xlsx?$/i.test(f.name));
        if (!file) { setError('Please drop a .xlsx tariff sheet.'); return; }
        setExcelFile(file);
        try {
            setProcessing('Parsing Excel...');
            const buf = await readArrayBuffer(file);
            const wb = XLSX.read(buf, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            // Read as rows of cells, not as records: these sheets carry the carrier's own
            // header block above the column names, so row one is not the header.
            const grid = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
            const { rows: parsed, meta } = readMetroTariffSheet(grid);
            const ports = [...new Set(parsed.map(r => String(r.PORT || '').trim()).filter(Boolean))];
            const modes = [...new Set(parsed.map(r => String(r.MODE || '').trim().toUpperCase()).filter(Boolean))];
            setRows(parsed);
            setSheetMeta(meta);
            // Offered rather than imposed - every one of these stays editable below.
            if (meta.supplier) setSupplierName(meta.supplier);
            if (meta.validFrom) setValidFrom(meta.validFrom);
            if (meta.validUntil) setValidUntil(meta.validUntil);
            const air = isAirSheet({ meta, modes });
            setSheetInfo({ ports, modes, rowCount: parsed.length, air });
            // Auto-resolve the source location from the sheet's PORT column — against airports
            // when the sheet is air trucking, against seaports otherwise.
            const pName = ports[0] || '';
            setPortName(pName);
            setPortCode((air ? AIRPORT_CODE_MAP : PORT_CODE_MAP)[pName.toUpperCase()] || '');
            setProcessing(null);
        } catch (e) {
            setError(e.message || 'Failed to parse the Excel file.');
            setProcessing(null);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls'] },
        disabled: !!processing,
    });

    function onConvert() {
        setError(null); setResult(null); setAddResult(null);
        if (!rows) { setError('Drop a tariff sheet first.'); return; }
        const code = portCode.trim().toUpperCase();
        const air = !!sheetInfo?.air;
        if (air && !/^[A-Z]{3}$/.test(code)) { setError('Enter a valid 3-letter IATA code for the source airport (e.g. MEL for Melbourne).'); return; }
        if (!air && !/^[A-Z]{5}$/.test(code)) { setError('Enter a valid 5-letter UN/LOCODE for the source port (e.g. AUBNE for Brisbane).'); return; }
        // A UN/LOCODE carries its country in the first two letters; an IATA code does not, so the
        // airport's country is looked up rather than sliced (MEL would otherwise read as ME).
        const countryCode = air ? AIRPORT_COUNTRY[code] : code.substr(0, 2);
        if (air && !countryCode) { setError(`Unknown airport code "${code}". Add it to AIRPORT_COUNTRY so its country can be set.`); return; }
        if (!directions.length) { setError('Choose at least one direction to import.'); return; }
        const port = {
            type: 'port', id: code,
            text: `${code} - ${portName || code}`,
            countryCode,
        };
        try {
            const res = convertMetroTariff({
                rows, port,
                fuelSurchargePct: parseFloat(fuelPct) || 0,
                fclBookingFee: parseFloat(fclBookingFee) || 0,
                fclBookingFeeCurrency: bookingCurrency.trim().toUpperCase() || 'AUD',
                validFrom, validUntil, directions,
                supplier: { organization: supplierName.trim() || 'Metro Tariff' },
            });
            setResult(res);
            setRates(res.rates);
            setPreviewOpen(true);
        } catch (e) {
            setError(e.message || 'Conversion failed.');
        }
    }

    async function onAdd() {
        if (!orgId || !rates.length) return;
        setAddResult(null);
        let ok = 0, failed = 0, deleted = 0;
        try {
            if (overrideExisting) {
                setAdding('Loading existing rates...');
                const existing = (await getRatesFromServer({ mode: 'rates', accesstoken, organizationId: orgId, since: 0 })).filter(r => !r.deleted);
                // Override key: same source+destination (exact) among metro-tariff rates.
                const keys = new Set(rates.map(r => `${r.source?.id}>${r.destination?.id}`));
                const toDelete = existing.filter(e => e.attributes?.source === 'metro-tariff' && keys.has(`${e.source?.id}>${e.destination?.id}`));
                if (toDelete.length) {
                    setAdding(`Removing ${toDelete.length} existing metro rate(s)...`);
                    for (let i = 0; i < toDelete.length; i += CHUNK) {
                        await axios.post(`/api/rates/admin-delete?organizationId=${orgId}&accesstoken=${accesstoken}`, { rates: toDelete.slice(i, i + CHUNK) });
                    }
                    deleted = toDelete.length;
                }
            }
            setAdding(`Adding 0 / ${rates.length}...`);
            for (let i = 0; i < rates.length; i += CHUNK) {
                const { data } = await axios.post(`/api/rates/bulk?organizationId=${orgId}&accesstoken=${accesstoken}`, { rates: rates.slice(i, i + CHUNK) });
                ok += (data.successful || []).length;
                failed += (data.failed || []).length;
                setAdding(`Adding ${Math.min(i + CHUNK, rates.length)} / ${rates.length}...`);
            }
            const org = (orgs || []).find(o => o.id === orgId);
            setAddResult({ ok, failed, deleted, org: org?.alias || orgId });
            setAdding(null);
        } catch (e) {
            // Name the workspace here too: a part-finished run needs to say where it got to.
            const org = (orgs || []).find(o => o.id === orgId);
            setAddResult({ ok, failed, deleted, org: org?.alias || orgId, error: e.response?.data?.detail || e.message });
            setAdding(null);
        }
    }

    const counts = result?.counts;
    const previewRates = useMemo(() => rates.slice(0, PREVIEW_LIMIT), [rates]);

    return (
        <Box sx={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
            <Typography variant='h4' sx={{ mb: 1 }}>Metro Tariff Import</Typography>
            <Typography variant='body1' sx={{ mb: 3, color: 'text.secondary' }}>
                Upload a per-suburb metro trucking sheet (port ↔ door). Each row becomes import + export
                rates bound to a postcode/named-location. LCL uses the W/M rate (per m³ or per tonne, whichever
                is greater — density 1:1000); FCL is per container (20′/40′); AIR is per kg. A sheet-level fuel
                surcharge % and an FCL per-container booking fee are added as surcharge lines.
            </Typography>

            {error && <Alert severity='error' sx={{ mb: 3 }}>{error}</Alert>}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box {...getRootProps()} sx={{
                        border: '2px dashed', borderColor: isDragActive ? 'primary.main' : 'grey.300', borderRadius: '8px',
                        p: '40px', textAlign: 'center', cursor: processing ? 'not-allowed' : 'pointer',
                        backgroundColor: isDragActive ? 'action.hover' : 'background.paper', opacity: processing ? 0.6 : 1,
                    }}>
                        <input {...getInputProps()} />
                        <FontAwesomeIcon icon={faCloudArrowUp} style={{ fontSize: 44, color: '#1976d2', marginBottom: 12 }} />
                        <Typography variant='body1'>Drag & drop the metro tariff <b>.xlsx</b> here, or click to select</Typography>
                    </Box>

                    {sheetInfo && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip color='success' label={`${excelFile?.name} — ${sheetInfo.rowCount} rows`} />
                            <Chip variant='outlined' label={`Mode(s): ${sheetInfo.modes.join(', ') || '—'}`} />
                            <Chip variant='outlined' label={`Port(s) in sheet: ${sheetInfo.ports.join(', ') || '—'}`} />
                            {sheetMeta?.supplierCode && <Chip variant='outlined' label={`Supplier code: ${sheetMeta.supplierCode}`} />}
                            {sheetMeta?.validity && <Chip variant='outlined' label={`Validity in sheet: ${sheetMeta.validity}`} />}
                            {sheetMeta?.service && <Chip variant='outlined' label={sheetMeta.service} />}
                        </Box>
                    )}

                    {rows && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField size='small' label='Supplier' value={supplierName} onChange={e => setSupplierName(e.target.value)} placeholder='Metro Tariff' sx={{ width: 180 }}
                                helperText={sheetMeta?.supplier ? 'from the sheet' : ' '} />
                            <TextField size='small' label={sheetInfo?.air ? 'Source airport name' : 'Source port name'} value={portName} onChange={e => setPortName(e.target.value)} sx={{ width: 180 }} />
                            {/* An air sheet's origin is an airport, named by its 3-letter IATA code; a sea
                                sheet's is a seaport, named by its 5-letter UN/LOCODE. Same field, and the
                                sheet says which it is, so the label and the check follow the sheet. */}
                            <TextField size='small' label={sheetInfo?.air ? 'Airport IATA code' : 'Port UN/LOCODE'} value={portCode} onChange={e => setPortCode(e.target.value.toUpperCase())} placeholder={sheetInfo?.air ? 'MEL' : 'AUBNE'} sx={{ width: 130 }}
                                error={!!portCode && !(sheetInfo?.air ? /^[A-Z]{3}$/ : /^[A-Z]{5}$/).test(portCode)}
                                helperText={portCode && !(sheetInfo?.air ? /^[A-Z]{3}$/ : /^[A-Z]{5}$/).test(portCode) ? (sheetInfo?.air ? '3 letters' : '5 letters') : ' '} />
                            <TextField size='small' label='Fuel surcharge %' type='number' value={fuelPct} onChange={e => setFuelPct(e.target.value)} sx={{ width: 130 }} />
                            <TextField size='small' label='FCL booking fee' type='number' value={fclBookingFee} onChange={e => setFclBookingFee(e.target.value)} sx={{ width: 130 }} />
                            <TextField size='small' label='Booking fee CUR' value={bookingCurrency} onChange={e => setBookingCurrency(e.target.value.toUpperCase())} sx={{ width: 110 }} />
                            <TextField size='small' label='Valid from' type='date' value={validFrom} onChange={e => setValidFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                            <TextField size='small' label='Valid until' type='date' value={validUntil} onChange={e => setValidUntil(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {[
                                        ['import', sheetInfo?.air ? 'Import (airport → door)' : 'Import (port → door)'],
                                        ['export', sheetInfo?.air ? 'Export (door → airport)' : 'Export (door → port)'],
                                    ].map(([dir, label]) => (
                                        <FormControlLabel
                                            key={dir}
                                            sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                                            control={(
                                                <Checkbox
                                                    size='small'
                                                    checked={directions.includes(dir)}
                                                    onChange={e => setDirections(d => (
                                                        e.target.checked ? [...d, dir] : d.filter(x => x !== dir)
                                                    ))}
                                                />
                                            )}
                                            label={label}
                                        />
                                    ))}
                                </Box>
                                <FormHelperText error={!directions.length}>
                                    {directions.length ? ' ' : 'Pick at least one'}
                                </FormHelperText>
                            </Box>
                            <Box sx={{ flexGrow: 1 }} />
                            <Button variant='contained' disabled={!!processing || !directions.length} onClick={onConvert}>
                                {processing ? <><CircularProgress size={16} sx={{ mr: 1 }} />{processing}</> : 'Convert'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth='xl' fullWidth>
                <DialogTitle>Converted metro rates preview</DialogTitle>
                <DialogContent dividers>
                    {counts && (
                        <Alert severity={result.errors?.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
                            <b>{rates.length}</b> rates from <b>{counts.LCL + counts.FCL + counts.AIR}</b> priced rows
                            &nbsp;|&nbsp; LCL <b>{counts.LCL}</b> · FCL <b>{counts.FCL}</b> · AIR <b>{counts.AIR}</b>
                            {counts.skipped ? <> &nbsp;|&nbsp; <span style={{ color: '#b26a00' }}>skipped {counts.skipped}</span></> : null}
                            {result.errors?.length ? <div style={{ marginTop: 4, fontSize: 12, color: '#b26a00' }}>{result.errors.slice(0, 5).map(e => `row ${e.row}: ${e.reason}`).join('; ')}{result.errors.length > 5 ? ` … +${result.errors.length - 5} more` : ''}</div> : null}
                        </Alert>
                    )}

                    <TableContainer component={Paper} variant='outlined' sx={{ maxHeight: 460, overflow: 'auto' }}>
                        <Table size='small' stickyHeader sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 } }}>
                            <TableHead>
                                <TableRow>
                                    {['Dir', 'Source', 'Destination', 'Product', 'Transport', 'Fuel', 'Booking fee'].map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {previewRates.map((r, i) => (
                                    <TableRow key={r.id || i} hover>
                                        <TableCell>{r.attributes?.metroDirection}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.source?.id}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.destination?.id}</TableCell>
                                        <TableCell>{r.product?.type}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{fieldDisplay(findField(r, 'TRANSPORT'))}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{fieldDisplay(findField(r, 'FUEL SURCHARGE'))}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{fieldDisplay(findField(r, 'PORT BOOKING FEE'))}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant='caption' color='text.secondary'>
                        {rates.length > PREVIEW_LIMIT ? `Showing first ${PREVIEW_LIMIT} of ${rates.length} rates.` : `${rates.length} rates.`}
                    </Typography>
                </DialogContent>
                {/* Outside the scrolling content on purpose: the preview runs to hundreds of
                    rows, and a result reported under it is a result nobody sees. */}
                {addResult && (
                    <Alert
                        severity={addResult.error || addResult.failed ? 'warning' : 'success'}
                        sx={{ mx: 3, mb: 1, borderRadius: 1 }}
                    >
                        {addResult.error
                            ? <>Could not add the rates{addResult.org ? <> to <b>{addResult.org}</b></> : null} — {addResult.error}</>
                            : <>
                                {addResult.deleted ? <>Overrode <b>{addResult.deleted}</b> existing and a</> : 'A'}dded{' '}
                                <b>{addResult.ok}</b> rate{addResult.ok === 1 ? '' : 's'}
                                {addResult.org ? <> to <b>{addResult.org}</b></> : null}
                                {addResult.failed ? <>. <b>{addResult.failed}</b> failed</> : null}
                                . They are searchable in that workspace now.
                            </>}
                    </Alert>
                )}
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <FormControl size='small' sx={{ minWidth: 260 }} disabled={!orgs || !!adding}
                        error={!!orgsError}
                    >
                        <InputLabel>Target workspace</InputLabel>
                        <Select label='Target workspace' value={orgId} onChange={e => setOrgId(e.target.value)}>
                            {(orgs || []).map(o => <MenuItem key={o.id} value={o.id}>{o.title || o.alias}</MenuItem>)}
                        </Select>
                        {orgsError && <FormHelperText>{orgsError}</FormHelperText>}
                    </FormControl>
                    <FormControlLabel
                        sx={{ ml: 1 }}
                        control={<Checkbox checked={overrideExisting} onChange={e => setOverrideExisting(e.target.checked)} />}
                        title='Delete existing metro-tariff rates for the same port↔door before adding'
                        label={<Typography variant='caption'>Override existing metro rates (same source · destination)</Typography>}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button onClick={() => setPreviewOpen(false)} color='inherit'>Close</Button>
                    <Button variant='contained' color='primary' disabled={!orgId || !!adding} onClick={onAdd}>
                        {adding ? <><CircularProgress size={16} sx={{ mr: 1 }} />{adding}</> : `${overrideExisting ? 'Override & add' : 'Add'} ${rates.length} rates`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default MetroTariff;
